import { SeverityEnum } from './general.interface';
import { ThreeStepProgressBarValues } from '../components/three-step-progress-bar/models/three-step-progress-bar.model';

export interface SystemSecurityScore {
  covered: number;
  completed: number;
  newAdded: number;
  severity: SeverityEnum;
  target: number;
}

export interface SecurityPlanRiskCoverage {
  coveredRisks: number;
  completedInProgram: number;
  newCovered: number;
  existingCovered: number;
  uncompletedRisks: number;
  totalRisks: number;
  percentageCovered: number;
}

export interface ContractualCompliance {
  frameworkName: string;
  frameworkId: string;
  isNA: boolean;
  // unified
  totalControls: number;
  covered: number;
  existingCovered: number;
  newCovered: number;
  uncompleted: number;
  completedInProgram: number;
  percentageCovered: number;
}

export interface CriticalSystems {
  financial: ProgressMetric;
  coreBusiness: ProgressMetric;
  ot: ProgressMetric;
  infrastructure: ProgressMetric;
}

export interface SecurityPlan {
  id: string;
  name: string;
  operationStartDate: string;
  operationEndDate: string;
  planLength: number;
  complianceCoverage: SecurityScore;
  riskCoverage: SecurityScore;
  systemScore: SecurityScore;
  /**
   * FE only: the three columns above mapped to three-step bar values, or null when there is
   * nothing to draw. Not sent by the BE. Feature 2708 / FE task 2841.
   */
  complianceCoverageBar?: ThreeStepProgressBarValues | null;
  riskCoverageBar?: ThreeStepProgressBarValues | null;
  systemScoreBar?: ThreeStepProgressBarValues | null;
  /** False when the plan holds no Mitigation risk - the row renders 'Inactive'. */
  hasMitigatedRisks?: boolean;
  budget: number;
  status: PlanStatus;
  budgetType?: SecurityPlanBudgetType;
  presetType?: SecurityPlanPresetBudgetType;
  showGreenBorder?: boolean;
  showRedBorder?: boolean;
}

export interface PlanStatusDropdownOption {
  id: PlanStatus;
  name: string;
  description: string;
  isSelectable: boolean;
}

export enum PlanStatus {
  DRAFT = 1,
  APPROVED,
  IN_PROGRESS,
  COMPLETED,
}

/**
 * A plan row's System score, Compliance coverage or Risk mitigation as a three-step bar. All values are integer percent 0..100, or null
 * when the step does not exist for the row or was never measured. The steps are time-ordered
 * (before -> planned / achieved -> target), never value-ordered: a step lower than the one
 * before it is a regression.
 * Feature 2708 / FE task 2841.
 */
export interface SecurityScore {
  /**
   * 'Before' step. Draft / Approved: the current org score. In Progress / Completed: the org
   * score when the plan started (snapshot); null when there is no snapshot.
   */
  previouslyCompleted: number | null;
  /**
   * 'Planned' step, Draft / Approved only: the target of the Approved plan running right before
   * this one. Null when there is no such plan, or for In Progress / Completed / Inactive rows.
   */
  planned: number | null;
  /**
   * 'New progress' step, In Progress / Completed only. In Progress: the current org score.
   * Completed: the org score on the plan's last day. Null for Draft / Approved rows, or a
   * Completed plan whose end was never recorded.
   */
  newProgress: number | null;
  /**
   * 'Target' step: where the org lands if everything in this plan is completed (frozen on the
   * last day for Completed plans). Null for a plan completed before that snapshot existed.
   * Inactive rows: equals previouslyCompleted.
   */
  target: number | null;
}

export interface ProgressMetric {
  name: string;
  covered: number;
  completed: number;
  isNA?: boolean;
}

export interface ComplianceCoverage {
  frameworks: ContractualCompliance[];
}

export interface LocalLawsProtection {
  dataTypes: LocalLawsProtectionItem[];
}

export interface LocalLawsProtectionItem {
  dataTypeName: string;
  dataTypeId: string;
  isNA: boolean;
  // unified
  totalControls: number;
  covered: number;
  existingCovered: number;
  newCovered: number;
  uncompleted: number;
  completedInProgram: number;
  percentageCovered: number;
  // legacy — delete after backend removes these fields
  coveredControls: number;
  uncompletedControls: number;
  currentlyCovered: number;
}

export const PlanStatusLabels = {
  [PlanStatus.DRAFT]: 'Draft',
  [PlanStatus.IN_PROGRESS]: 'In Progress',
  [PlanStatus.COMPLETED]: 'Completed',
  [PlanStatus.APPROVED]: 'Approved',
};

export interface SecurityPlanDetails {
  id?: string;
  name: string;
  startingDate: string;
  planLength: number;
  budgetType: SecurityPlanBudgetType;
  budgetAmount: number;
  presetType: SecurityPlanPresetBudgetType;
  status: SecurityPlanDetailsStatus;
  /** Remediation / plan strategy selection when provided by the API. */
  selectedStrategyOptionId?: StrategyOption;
  selectedControlPrioritizationOptionId: ControlPrioritizationOption;
}

export enum ControlPrioritizationOption {
  BEST_IN_RISK = 1,
  BEST_FOR_ORG = 2,
}

export enum StrategyOption {
  MAX_AFFECT = 1,
  MIN_COST = 2,
}

export interface SecurityPlanDetailsStatus {
  id: number;
  name: string;
}

export interface CreateUpdateSecurityPlan {
  id?: string;
  name: string;
  startingDate: string;
  planLength: number;
  budgetTypeId: number;
  budgetAmount?: number;
  presetTypeId?: number;
  statusId: number;
  selectedStrategyOptionId?: StrategyOption;
  selectedControlPrioritizationOptionId?: ControlPrioritizationOption;
  resetOutOfScopeDates?: boolean;
}

export interface SecurityPlanBudgetType {
  id: SecurityPlanBudgetTypeEnum;
  name: string;
}

export enum SecurityPlanBudgetTypeEnum {
  PRE_SET = 1,
  ACCUMULATING,
}

export enum SecurityPlanPresetBudgetTypeEnum {
  YEARLY = 1,
  QUARTERLY,
}

export interface SecurityPlanPresetBudgetType {
  id: SecurityPlanPresetBudgetTypeEnum;
  name: string;
}

export interface DatesOverlap {
  status: PlanStatusDropdownOption;
  conflicts: {
    planId: string;
    name: string;
    startingDate: string;
    endDate: string;
  }[];
  messageId: string;
  hasOverlap: boolean;
}

export interface PlanStrategyOption<T> {
  id: T;
  /** Canonical English name (Task 2531 T177 / M4A). */
  name: string;
  nameHe?: string;
  nameAr?: string;
  description: string;
  descriptionHe?: string;
  descriptionAr?: string;
}
